import { ReactNode, useCallback, useMemo } from "react";
import classNames from "classnames";
import { noop } from "@common/helpers/function";
import {
    ScheduleBillingStatus,
    ScheduleSourceType,
} from "@common/types/domains/accounting/schedule";
import {
    BillingIcon,
    CalendarIcon,
    ClassIcon,
    DollarCircleIcon,
    FolderIcon,
    MembersIcon,
    ReceiptIcon,
    StatusIcon,
    TemplatesIcon,
    TriangleIcon,
} from "@icons";
import { formatDateOnly } from "@helpers/date";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import { AggregatedFilters } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters";
import { AggregatedFiltersOverlay } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersOverlay";
import { AggregatedFiltersProvider } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersProvider";
import { useAggregatedFiltersContext } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters.context";
import { ActiveFilterButton } from "@components/pageHeader/filters/ActiveFilters/ActiveFilterButton";
import { ActiveFilters } from "@components/pageHeader/filters/ActiveFilters/ActiveFilters";
import { formatFilterLabelText } from "@components/pageHeader/filters/ActiveFilters/formatFilterLabel";
import { FilterNavTab } from "@components/pageHeader/filters/FilterNavTab";
import { FilterTabPanel } from "@components/pageHeader/filters/FilterTabPanel";
import { ClassFilterOverlay } from "@components/Class/ClassFilter/ClassFilterOverlay";
import { useClasses } from "@api/class.api";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import overlayStyles from "@components/pageHeader/filters/FiltersOverlay.module.scss";
import statusStyles from "@components/pageHeader/filters/Tabs/TransactionStatusFilter/TransactionStatusFilter.module.scss";
import {
    SCHEDULE_BILLING_STATUS_LABELS,
    SCHEDULE_SOURCE_TYPE_LABELS,
} from "@common/helpers/domains/accounting/scheduleApplicationFilters";
import {
    ScheduleApplicationTemplateOption,
    ScheduleApplicationsFilters,
} from "./scheduleApplicationRowHelpers";
import {
    SCHEDULE_LIFECYCLE_FILTER_STATUS_LABELS,
    ScheduleLifecycleFilterStatus,
} from "./scheduleApplicationStatusDisplay";
import { resolvePolicyFilterLabel } from "../policyFilterOptions";
import { isStringFilterValue } from "@components/pageHeader/filters/FilterMultiselect/isStringFilterValue";
import { getSubModeFilterLabel } from "../scheduleSubModeFilters";
import { useScheduleHubCopy } from "../ScheduleHubScopeProvider";

const TEMPLATE_TAB = "template";
const GROUP_TAB = "group";
const TYPE_TAB = "type";
const SUB_MODE_TAB = "subMode";
const LIFECYCLE_TAB = "lifecycle";
const BILLING_TAB = "billing";
const COUNTERPARTY_TAB = "counterparty";
const LINE_ITEM_TAB = "lineItem";
const CLASS_TAB = "class";
const PERIOD_TAB = "period";

type ScheduleApplicationsFilterTab =
    | typeof TEMPLATE_TAB
    | typeof GROUP_TAB
    | typeof TYPE_TAB
    | typeof SUB_MODE_TAB
    | typeof LIFECYCLE_TAB
    | typeof BILLING_TAB
    | typeof COUNTERPARTY_TAB
    | typeof LINE_ITEM_TAB
    | typeof CLASS_TAB
    | typeof PERIOD_TAB;

const LIFECYCLE_STATUS_ORDER: ScheduleLifecycleFilterStatus[] = [
    ScheduleLifecycleFilterStatus.active,
    ScheduleLifecycleFilterStatus.modified,
    ScheduleLifecycleFilterStatus.paused,
    ScheduleLifecycleFilterStatus.stopped,
    ScheduleLifecycleFilterStatus.completed,
    ScheduleLifecycleFilterStatus.unwound,
];

const LIFECYCLE_OPTIONS: FilterMultiselectOption[] = LIFECYCLE_STATUS_ORDER.map(
    (status) => ({
        value: status,
        label: SCHEDULE_LIFECYCLE_FILTER_STATUS_LABELS[status],
    }),
);

function isLifecycleStatus(
    value: FilterMultiselectValue,
): value is ScheduleLifecycleFilterStatus {
    return (
        typeof value === "string" &&
        LIFECYCLE_STATUS_ORDER.some((status) => status === value)
    );
}

const BILLING_STATUSES = Object.values(ScheduleBillingStatus);

const BILLING_OPTIONS: FilterMultiselectOption[] = BILLING_STATUSES.map(
    (status) => ({
        value: status,
        label: SCHEDULE_BILLING_STATUS_LABELS[status],
    }),
);

function isBillingStatus(
    value: FilterMultiselectValue,
): value is ScheduleBillingStatus {
    return (
        typeof value === "string" &&
        BILLING_STATUSES.some((status) => status === value)
    );
}

const CLASS_FILTER_BEHAVIOUR = {
    selectedParentClassIncludesAllChildren: true,
    canSelectOnlyOneClassInTheSameRoot: false,
};

const SOURCE_TYPE_ORDER: ScheduleSourceType[] = [
    ScheduleSourceType.transaction,
    ScheduleSourceType.invoice,
    ScheduleSourceType.manual,
];

const SOURCE_OPTIONS: FilterMultiselectOption[] = SOURCE_TYPE_ORDER.map(
    (sourceType) => ({
        value: sourceType,
        label: SCHEDULE_SOURCE_TYPE_LABELS[sourceType],
    }),
);

function isSourceType(
    value: FilterMultiselectValue,
): value is ScheduleSourceType {
    return (
        typeof value === "string" &&
        SOURCE_TYPE_ORDER.some((sourceType) => sourceType === value)
    );
}

const ScheduleApplicationsFilterNavTab = ({
    tabKey,
    label,
    icon,
}: Readonly<{
    tabKey: ScheduleApplicationsFilterTab;
    label: string;
    icon: ReactNode;
}>) => (
    <FilterNavTab eventKey={tabKey} className={overlayStyles.navigationButton}>
        <span className={overlayStyles.navigationItem}>
            <span className={overlayStyles.navigationIcon}>{icon}</span>
            {label}
            <TriangleIcon
                className={classNames(
                    overlayStyles.navigationActiveIndicator,
                    "icon-color-gray-500",
                )}
            />
        </span>
    </FilterNavTab>
);

const ScheduleApplicationsFilterPanel = ({
    tabKey,
    name,
    options,
    value,
    onChange,
    isSearchable,
}: Readonly<{
    tabKey: ScheduleApplicationsFilterTab;
    name: string;
    options: FilterMultiselectOption[];
    value: FilterMultiselectValue[];
    onChange: (values: FilterMultiselectValue[]) => void;
    isSearchable?: boolean;
}>) => (
    <FilterTabPanel eventKey={tabKey}>
        {/**
         * The drawer viewport owns a Base UI swipe-to-dismiss gesture whose
         * onPointerDown calls setPointerCapture. This popover is portaled into
         * the drawer, so a press on an option starts that gesture, pointerup is
         * retargeted to the drawer, and the option never receives a click.
         */}
        <div
            className={statusStyles.filter}
            onPointerDown={(event) => event.stopPropagation()}
        >
            <FilterMultiselect
                inDropdown={false}
                searchable={isSearchable}
                options={options}
                value={value}
                onChange={onChange}
                name={name}
                onBlur={noop}
            />
        </div>
    </FilterTabPanel>
);

const ScheduleApplicationsActiveFilterChip = ({
    tabKey,
    labels,
    pluralNoun,
    onReset,
}: Readonly<{
    tabKey: ScheduleApplicationsFilterTab;
    labels: string[];
    pluralNoun: string;
    onReset: () => void;
}>) => {
    const { open } = useAggregatedFiltersContext();
    const text = formatFilterLabelText(labels, pluralNoun);

    if (text === null) {
        return null;
    }

    return (
        <ActiveFilterButton
            openFilter={() => open(tabKey)}
            resetFilter={onReset}
            label={text}
        />
    );
};

interface ScheduleApplicationsFiltersControlsProps {
    filters: ScheduleApplicationsFilters;
    onFiltersChange: (filters: ScheduleApplicationsFilters) => void;
    counterpartyNames: string[];
    lineItemLabels: string[];
    /** Policies to offer in the Policy tab; omitted when the list is already scoped to one policy. */
    templateOptions?: ScheduleApplicationTemplateOption[];
    groupNames: string[];
    portalContainer?: HTMLElement;
}

/**
 * "Add filter" popover + active-filter chips for the schedule applications
 * drill-down. Built on the shared `AggregatedFilters` primitives rather than
 * `TablePageHeader`'s `availableFilters` because the shared `FilterTabs` enum
 * has no schedule-application concepts.
 */
export const ScheduleApplicationsFiltersControls = ({
    filters,
    onFiltersChange,
    counterpartyNames,
    lineItemLabels,
    templateOptions,
    groupNames,
    portalContainer,
}: ScheduleApplicationsFiltersControlsProps) => {
    const { classesMap } = useClasses();
    const hasTemplateTab = templateOptions !== undefined;
    const { subModeFilter } = useScheduleHubCopy();
    const templateMultiselectOptions = useMemo<FilterMultiselectOption[]>(
        () =>
            (templateOptions ?? []).map((option) => ({
                value: option.id,
                label: option.name,
            })),
        [templateOptions],
    );
    const activeFilters = useMemo<ScheduleApplicationsFilterTab[]>(() => {
        const active: ScheduleApplicationsFilterTab[] = [];
        if (filters.templateIds.length > 0) {
            active.push(TEMPLATE_TAB);
        }
        if (filters.groups.length > 0) {
            active.push(GROUP_TAB);
        }
        if (filters.sourceTypes.length > 0) {
            active.push(TYPE_TAB);
        }
        if (subModeFilter.getValues(filters).length > 0) {
            active.push(SUB_MODE_TAB);
        }
        if (filters.lifecycleStatuses.length > 0) {
            active.push(LIFECYCLE_TAB);
        }
        if (filters.billingStatuses.length > 0) {
            active.push(BILLING_TAB);
        }
        if (filters.counterpartyNames.length > 0) {
            active.push(COUNTERPARTY_TAB);
        }
        if (filters.lineItemLabels.length > 0) {
            active.push(LINE_ITEM_TAB);
        }
        if (filters.classIds.length > 0) {
            active.push(CLASS_TAB);
        }
        if (filters.periodStart || filters.periodEnd) {
            active.push(PERIOD_TAB);
        }
        return active;
    }, [filters, subModeFilter]);
    const counterpartyOptions = useMemo<FilterMultiselectOption[]>(
        () => counterpartyNames.map((name) => ({ value: name, label: name })),
        [counterpartyNames],
    );
    const lineItemOptions = useMemo<FilterMultiselectOption[]>(
        () => lineItemLabels.map((label) => ({ value: label, label })),
        [lineItemLabels],
    );
    const groupOptions = useMemo<FilterMultiselectOption[]>(
        () => groupNames.map((name) => ({ value: name, label: name })),
        [groupNames],
    );

    const handleTemplateValuesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange({
                ...filters,
                templateIds: values.filter(isStringFilterValue),
            }),
        [filters, onFiltersChange],
    );

    const handleLifecycleValuesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange({
                ...filters,
                lifecycleStatuses: values.filter(isLifecycleStatus),
            }),
        [filters, onFiltersChange],
    );

    const handleBillingValuesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange({
                ...filters,
                billingStatuses: values.filter(isBillingStatus),
            }),
        [filters, onFiltersChange],
    );

    const handleCounterpartyValuesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange({
                ...filters,
                counterpartyNames: values.filter(isStringFilterValue),
            }),
        [filters, onFiltersChange],
    );

    const handleLineItemValuesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange({
                ...filters,
                lineItemLabels: values.filter(isStringFilterValue),
            }),
        [filters, onFiltersChange],
    );

    const handleClassIdsChange = useCallback(
        (classIds: string[]) => onFiltersChange({ ...filters, classIds }),
        [filters, onFiltersChange],
    );
    const handlePeriodChange = useCallback(
        (value: { start?: string; end?: string }, isAllTime?: boolean) =>
            onFiltersChange({
                ...filters,
                periodStart: isAllTime ? null : (value.start ?? null),
                periodEnd: isAllTime ? null : (value.end ?? null),
            }),
        [filters, onFiltersChange],
    );

    const handleGroupValuesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange({
                ...filters,
                groups: values.filter(isStringFilterValue),
            }),
        [filters, onFiltersChange],
    );

    const handleSourceValuesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange({
                ...filters,
                sourceTypes: values.filter(isSourceType),
            }),
        [filters, onFiltersChange],
    );

    const handleSubModesChange = useCallback(
        (values: FilterMultiselectValue[]) =>
            onFiltersChange(subModeFilter.setValues(filters, values)),
        [filters, onFiltersChange, subModeFilter],
    );

    const clearFilters = useCallback(
        () =>
            onFiltersChange({
                ...filters,
                templateIds: [],
                groups: [],
                sourceTypes: [],
                revenueSubModes: [],
                expenseSubModes: [],
                lifecycleStatuses: [],
                billingStatuses: [],
                counterpartyNames: [],
                lineItemLabels: [],
                classIds: [],
                periodStart: null,
                periodEnd: null,
            }),
        [filters, onFiltersChange],
    );

    return (
        <AggregatedFiltersProvider
            defaultTab={hasTemplateTab ? TEMPLATE_TAB : TYPE_TAB}
        >
            <AggregatedFilters
                label="Add filter"
                portalContainer={portalContainer}
            >
                <AggregatedFiltersOverlay
                    filterClassname={overlayStyles.filter}
                    navigation={
                        <>
                            {hasTemplateTab && (
                                <ScheduleApplicationsFilterNavTab
                                    tabKey={TEMPLATE_TAB}
                                    label="Policy"
                                    icon={<TemplatesIcon />}
                                />
                            )}
                            <ScheduleApplicationsFilterNavTab
                                tabKey={GROUP_TAB}
                                label="Group"
                                icon={<FolderIcon />}
                            />
                            <ScheduleApplicationsFilterNavTab
                                tabKey={TYPE_TAB}
                                label="Type"
                                icon={<ReceiptIcon />}
                            />
                            <ScheduleApplicationsFilterNavTab
                                tabKey={SUB_MODE_TAB}
                                label={subModeFilter.tabLabel}
                                icon={<DollarCircleIcon />}
                            />
                            <ScheduleApplicationsFilterNavTab
                                tabKey={LIFECYCLE_TAB}
                                label="Status"
                                icon={<StatusIcon />}
                            />
                            <ScheduleApplicationsFilterNavTab
                                tabKey={BILLING_TAB}
                                label="Billing"
                                icon={<BillingIcon />}
                            />
                            <ScheduleApplicationsFilterNavTab
                                tabKey={COUNTERPARTY_TAB}
                                label="Counterparty"
                                icon={<MembersIcon />}
                            />
                            <ScheduleApplicationsFilterNavTab
                                tabKey={LINE_ITEM_TAB}
                                label="Line item"
                                icon={<ReceiptIcon />}
                            />
                            <ScheduleApplicationsFilterNavTab
                                tabKey={CLASS_TAB}
                                label="Class"
                                icon={<ClassIcon />}
                            />
                            <ScheduleApplicationsFilterNavTab
                                tabKey={PERIOD_TAB}
                                label="Period"
                                icon={<CalendarIcon />}
                            />
                        </>
                    }
                    content={
                        <>
                            {hasTemplateTab && (
                                <ScheduleApplicationsFilterPanel
                                    tabKey={TEMPLATE_TAB}
                                    name="Policy"
                                    options={templateMultiselectOptions}
                                    value={filters.templateIds}
                                    onChange={handleTemplateValuesChange}
                                    isSearchable
                                />
                            )}
                            <ScheduleApplicationsFilterPanel
                                tabKey={GROUP_TAB}
                                name="Group"
                                options={groupOptions}
                                value={filters.groups}
                                onChange={handleGroupValuesChange}
                                isSearchable
                            />
                            <ScheduleApplicationsFilterPanel
                                tabKey={TYPE_TAB}
                                name="Type"
                                options={SOURCE_OPTIONS}
                                value={filters.sourceTypes}
                                onChange={handleSourceValuesChange}
                            />
                            <ScheduleApplicationsFilterPanel
                                tabKey={SUB_MODE_TAB}
                                name={subModeFilter.tabLabel}
                                options={subModeFilter.options}
                                value={subModeFilter.getValues(filters)}
                                onChange={handleSubModesChange}
                            />
                            <ScheduleApplicationsFilterPanel
                                tabKey={LIFECYCLE_TAB}
                                name="Status"
                                options={LIFECYCLE_OPTIONS}
                                value={filters.lifecycleStatuses}
                                onChange={handleLifecycleValuesChange}
                            />
                            <ScheduleApplicationsFilterPanel
                                tabKey={BILLING_TAB}
                                name="Billing"
                                options={BILLING_OPTIONS}
                                value={filters.billingStatuses}
                                onChange={handleBillingValuesChange}
                            />
                            <ScheduleApplicationsFilterPanel
                                tabKey={COUNTERPARTY_TAB}
                                name="Counterparty"
                                options={counterpartyOptions}
                                value={filters.counterpartyNames}
                                onChange={handleCounterpartyValuesChange}
                                isSearchable
                            />
                            <ScheduleApplicationsFilterPanel
                                tabKey={LINE_ITEM_TAB}
                                name="Line item"
                                options={lineItemOptions}
                                value={filters.lineItemLabels}
                                onChange={handleLineItemValuesChange}
                                isSearchable
                            />
                            <FilterTabPanel eventKey={CLASS_TAB}>
                                {/* Same drawer swipe-gesture guard as the
                                    multiselect panels above. `statusStyles.filter`
                                    cancels the tab pane's own padding so the
                                    class tree runs edge to edge and fills the
                                    fixed-height pane, like every sibling tab. */}
                                <div
                                    className={statusStyles.filter}
                                    onPointerDown={(event) =>
                                        event.stopPropagation()
                                    }
                                >
                                    <ClassFilterOverlay
                                        selectedClasses={filters.classIds}
                                        onChange={handleClassIdsChange}
                                        behaviour={CLASS_FILTER_BEHAVIOUR}
                                    />
                                </div>
                            </FilterTabPanel>
                            <FilterTabPanel eventKey={PERIOD_TAB}>
                                <div
                                    className={statusStyles.filter}
                                    onPointerDown={(event) =>
                                        event.stopPropagation()
                                    }
                                >
                                    <DateRangeFilterNext
                                        name="period"
                                        dateRange={{
                                            start:
                                                filters.periodStart ??
                                                undefined,
                                            end: filters.periodEnd ?? undefined,
                                        }}
                                        onChange={handlePeriodChange}
                                    />
                                </div>
                            </FilterTabPanel>
                        </>
                    }
                />
            </AggregatedFilters>

            {activeFilters.length > 0 && (
                <ActiveFilters
                    activeFilters={activeFilters}
                    maxVisibleFilters={activeFilters.length}
                    clearFilters={clearFilters}
                    renderVisibleFilters={(visibleFilters) =>
                        visibleFilters.map((tabKey) => {
                            if (tabKey === TEMPLATE_TAB) {
                                return (
                                    <ScheduleApplicationsActiveFilterChip
                                        key={tabKey}
                                        tabKey={TEMPLATE_TAB}
                                        pluralNoun="Policies"
                                        labels={filters.templateIds.map(
                                            (templateId) =>
                                                resolvePolicyFilterLabel({
                                                    templateId,
                                                    policies:
                                                        templateOptions ?? [],
                                                }),
                                        )}
                                        onReset={() =>
                                            handleTemplateValuesChange([])
                                        }
                                    />
                                );
                            }
                            if (tabKey === GROUP_TAB) {
                                return (
                                    <ScheduleApplicationsActiveFilterChip
                                        key={tabKey}
                                        tabKey={GROUP_TAB}
                                        pluralNoun="Groups"
                                        labels={filters.groups}
                                        onReset={() =>
                                            handleGroupValuesChange([])
                                        }
                                    />
                                );
                            }
                            if (tabKey === TYPE_TAB) {
                                return (
                                    <ScheduleApplicationsActiveFilterChip
                                        key={tabKey}
                                        tabKey={TYPE_TAB}
                                        pluralNoun="Types"
                                        labels={filters.sourceTypes.map(
                                            (sourceType) =>
                                                SCHEDULE_SOURCE_TYPE_LABELS[
                                                    sourceType
                                                ],
                                        )}
                                        onReset={() =>
                                            handleSourceValuesChange([])
                                        }
                                    />
                                );
                            }
                            if (tabKey === SUB_MODE_TAB) {
                                return (
                                    <ScheduleApplicationsActiveFilterChip
                                        key={tabKey}
                                        tabKey={SUB_MODE_TAB}
                                        pluralNoun={subModeFilter.pluralNoun}
                                        labels={subModeFilter
                                            .getValues(filters)
                                            .map((value) =>
                                                getSubModeFilterLabel({
                                                    filter: subModeFilter,
                                                    value,
                                                }),
                                            )}
                                        onReset={() => handleSubModesChange([])}
                                    />
                                );
                            }
                            if (tabKey === LIFECYCLE_TAB) {
                                return (
                                    <ScheduleApplicationsActiveFilterChip
                                        key={tabKey}
                                        tabKey={LIFECYCLE_TAB}
                                        pluralNoun="Statuses"
                                        labels={filters.lifecycleStatuses.map(
                                            (status) =>
                                                SCHEDULE_LIFECYCLE_FILTER_STATUS_LABELS[
                                                    status
                                                ],
                                        )}
                                        onReset={() =>
                                            handleLifecycleValuesChange([])
                                        }
                                    />
                                );
                            }
                            if (tabKey === BILLING_TAB) {
                                return (
                                    <ScheduleApplicationsActiveFilterChip
                                        key={tabKey}
                                        tabKey={BILLING_TAB}
                                        pluralNoun="Billing statuses"
                                        labels={filters.billingStatuses.map(
                                            (status) =>
                                                SCHEDULE_BILLING_STATUS_LABELS[
                                                    status
                                                ],
                                        )}
                                        onReset={() =>
                                            handleBillingValuesChange([])
                                        }
                                    />
                                );
                            }
                            if (tabKey === CLASS_TAB) {
                                return (
                                    <ScheduleApplicationsActiveFilterChip
                                        key={tabKey}
                                        tabKey={CLASS_TAB}
                                        pluralNoun="Classes"
                                        labels={filters.classIds.map(
                                            (classId) =>
                                                classesMap.get(classId)
                                                    ?.label ?? classId,
                                        )}
                                        onReset={() => handleClassIdsChange([])}
                                    />
                                );
                            }
                            if (tabKey === LINE_ITEM_TAB) {
                                return (
                                    <ScheduleApplicationsActiveFilterChip
                                        key={tabKey}
                                        tabKey={LINE_ITEM_TAB}
                                        pluralNoun="Line items"
                                        labels={filters.lineItemLabels}
                                        onReset={() =>
                                            handleLineItemValuesChange([])
                                        }
                                    />
                                );
                            }
                            if (tabKey === PERIOD_TAB) {
                                const periodLabel = [
                                    filters.periodStart
                                        ? formatDateOnly(filters.periodStart)
                                        : null,
                                    filters.periodEnd
                                        ? formatDateOnly(filters.periodEnd)
                                        : null,
                                ]
                                    .filter(
                                        (value): value is string =>
                                            value !== null,
                                    )
                                    .join(" – ");
                                return (
                                    <ScheduleApplicationsActiveFilterChip
                                        key={tabKey}
                                        tabKey={PERIOD_TAB}
                                        pluralNoun="Periods"
                                        labels={[periodLabel]}
                                        onReset={() =>
                                            handlePeriodChange({}, true)
                                        }
                                    />
                                );
                            }
                            return (
                                <ScheduleApplicationsActiveFilterChip
                                    key={tabKey}
                                    tabKey={COUNTERPARTY_TAB}
                                    pluralNoun="Counterparties"
                                    labels={filters.counterpartyNames}
                                    onReset={() =>
                                        handleCounterpartyValuesChange([])
                                    }
                                />
                            );
                        })
                    }
                />
            )}
        </AggregatedFiltersProvider>
    );
};
